import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Server,
  AlertCircle,
  RefreshCw,
  MessageSquare,
  ArrowLeft,
} from 'lucide-react';
import PageHeader from '../components/headers/Header';
import { Box, Typography, Button, CircularProgress, Container, Paper } from '@mui/material';
import { createServerStatusSource, checkServerHealth } from '../utils/serverHealth';

const ServerDownPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error') === 'true';
  const [isChecking, setIsChecking] = useState(false);
  const [connectionKey, setConnectionKey] = useState(0);

  // Teams Group Chat Link
  const TEAMS_REPORT_LINK = "https://teams.microsoft.com/l/chat/";

  useEffect(() => {
    let isCancelled = false;
    const source = createServerStatusSource();

    const handleServerStatus = async (event) => {
      if (isCancelled) return;

      if (event.data === 'UP') {
        console.log('📡 SSE reported UP. Verifying server health...');
        const isHealthy = await checkServerHealth();
        
        if (isHealthy && !isCancelled) {
          console.log('✅ Server is healthy. Navigating to dashboard.');
          source.close();
          navigate('/dashboard', { replace: true });
        }
      }
    };

    source.addEventListener('server-status', handleServerStatus);
    source.onopen = () => {
      if (!isCancelled) setIsChecking(false);
    };
    source.onerror = () => {
      if (!isCancelled) setIsChecking(false);
    };

    return () => {
      isCancelled = true;
      source.removeEventListener('server-status', handleServerStatus);
      source.close();
    };
  }, [navigate, connectionKey]);

  const handleManualCheck = async () => {
    setIsChecking(true);
    const isHealthy = await checkServerHealth();
    if (isHealthy) {
      navigate('/dashboard', { replace: true });
    } else {
      setConnectionKey((key) => key + 1);
      setTimeout(() => setIsChecking(false), 1000);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/login');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      pt: 4
    }}>
      <Container maxWidth="md">
        <PageHeader
          title="Server Connection Issue"
          subtitle="We're having trouble reaching the server right now."
        />

        <Paper sx={{
          p: { xs: 4, md: 6 },
          borderRadius: '24px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
          border: '1px solid #e2e8f0',
          mt: 4
        }}>
          <Box sx={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            backgroundColor: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px'
          }}>
            <Server size={50} color="#dc2626" />
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
            Server is Offline
          </Typography>

          <Typography variant="body1" sx={{ color: '#64748b', mb: 6, maxWidth: '500px', mx: 'auto', lineHeight: 1.7, fontSize: '1.1rem' }}>
            The application is currently unable to communicate with the backend services. 
            This is usually a temporary issue while we perform maintenance or updates.
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            alignItems: 'center' 
          }}>
            {/* Primary Action: Report to Teams */}
            <Button
              variant="contained"
              size="large"
              component="a"
              href={TEAMS_REPORT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<MessageSquare size={22} />}
              sx={{
                backgroundColor: '#4b53bc', // Teams purple
                '&:hover': { backgroundColor: '#3b449b' },
                borderRadius: '14px',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
                boxShadow: '0 4px 14px rgba(75, 83, 188, 0.3)'
              }}
            >
              Report to IT Support
            </Button>

            {/* Secondary Action: Reconnect */}
            <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' }, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleManualCheck}
                disabled={isChecking}
                startIcon={isChecking ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
                sx={{
                  flex: 1,
                  borderRadius: '12px',
                  py: 1.5,
                  px: 3,
                  fontWeight: 600,
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' },
                  textTransform: 'none'
                }}
              >
                {isChecking ? 'Checking...' : 'Check Again'}
              </Button>

              <Button
                variant="text"
                onClick={handleGoBack}
                startIcon={<ArrowLeft size={20} />}
                sx={{
                  flex: 1,
                  borderRadius: '12px',
                  py: 1.5,
                  px: 3,
                  fontWeight: 600,
                  color: '#64748b',
                  '&:hover': { backgroundColor: '#f1f5f9' },
                  textTransform: 'none'
                }}
              >
                Go Back
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                backgroundColor: '#ef4444',
                animation: 'pulse 2s infinite'
              }} />
              <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                Monitoring server status...
              </Typography>
            </Box>
            <style>
              {`
                @keyframes pulse {
                  0% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.4; transform: scale(1.2); }
                  100% { opacity: 1; transform: scale(1); }
                }
              `}
            </style>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ServerDownPage;
